import React from 'react';

export const sidebarTopGap = 16;
const sidebarBottomGap = 64;

/**
 * The report can start below a schedule and toolbar inside the page scroll container.
 * Sticky CSS cannot calculate the remaining visible space in that container. Cap
 * the natural editor height to leave breathing room and scroll only the fields.
 */
export function useSidebarViewport() {
    const sidebarRef = React.useRef<HTMLElement>(null);
    const [maxHeight, setMaxHeight] = React.useState<number>();

    React.useLayoutEffect(() => {
        const sidebar = sidebarRef.current;
        if (!sidebar) {
            return;
        }

        // The page scrolls below its header; window height alone includes that header.
        let scrollParent = sidebar.parentElement;
        while (
            scrollParent &&
            !/(auto|scroll)/.test(getComputedStyle(scrollParent).overflowY)
        ) {
            scrollParent = scrollParent.parentElement;
        }
        const scrollTarget = scrollParent || window;
        const measure = () => {
            const bounds = sidebar.getBoundingClientRect();
            const viewport = scrollParent?.getBoundingClientRect();
            const top = Math.max(
                bounds.top,
                (viewport?.top ?? 0) + sidebarTopGap,
                sidebarTopGap
            );
            const bottom = Math.min(
                bounds.bottom,
                (viewport?.bottom ?? window.innerHeight) - sidebarBottomGap,
                window.innerHeight - sidebarBottomGap
            );
            setMaxHeight(Math.max(0, bottom - top));
        };
        let frame = 0;
        const scheduleMeasure = () => {
            cancelAnimationFrame(frame);
            frame = requestAnimationFrame(measure);
        };
        const observer = new ResizeObserver(scheduleMeasure);
        observer.observe(sidebar);
        if (scrollParent) {
            observer.observe(scrollParent);
        }
        scrollTarget.addEventListener('scroll', scheduleMeasure, {
            passive: true,
        });
        window.addEventListener('resize', scheduleMeasure);
        measure();

        return () => {
            observer.disconnect();
            cancelAnimationFrame(frame);
            scrollTarget.removeEventListener('scroll', scheduleMeasure);
            window.removeEventListener('resize', scheduleMeasure);
        };
    }, []);

    return { sidebarRef, maxHeight };
}
